/**
 * MDJ Ritual — Interactive Sliding Puzzle
 * Maison de Jarin / Kaarnika Foods
 *
 * assets/mdj-core.js
 *
 * Orchestrator. This is the only file that talks to the DOM section root
 * directly. It reads configuration off data-attributes, lazily boots the
 * puzzle when the section scrolls into view, and wires together the five
 * collaborating modules:
 *
 *   MDJRitual.Storage   (assets/mdj-storage.js) — best-time persistence
 *   MDJRitual.Effects   (assets/mdj-effects.js) — canvas steam/particles
 *   MDJRitual.Board     (assets/mdj-board.js)   — puzzle generation/state
 *   MDJRitual.Input     (assets/mdj-input.js)   — pointer/touch/keyboard
 *   MDJRitual.Shopify   (assets/mdj-shopify.js) — cart + reward integration
 *
 * All five attach themselves to a single shared namespace, window.MDJRitual,
 * to avoid polluting global scope with multiple top-level identifiers.
 *
 * Load order (see sections/mdj-ritual.liquid):
 *   storage -> effects -> input -> board -> shopify -> core
 *
 * core is loaded last because it is the only file that instantiates the
 * others; at parse time every constructor it needs is already registered
 * on window.MDJRitual.
 */

(function () {
  'use strict';

  window.MDJRitual = window.MDJRitual || {};

  /**
   * Difficulty -> shuffle depth. Higher shuffle counts produce harder
   * (but always solvable) starting positions because they are generated
   * by walking legal moves backward from the solved state.
   */
  var DIFFICULTY_SHUFFLE_MOVES = {
    easy: 40,
    medium: 90,
    hard: 160
  };

  /**
   * Controller for a single instance of the ritual puzzle section.
   * One instance is created per matching root element on the page —
   * in practice there is one per product page render, but the code does
   * not assume that, so it is safe if a merchant embeds it twice.
   */
  function MDJRitualController(rootEl) {
    this.root = rootEl;
    this.sectionId = rootEl.dataset.sectionId || '';

    this.board = null;
    this.input = null;
    this.effects = null;
    this.shopify = null;
    this.storage = null;

    this.observer = null;
    this.booted = false;
    this.destroyed = false;

    this.timerRAF = null;
    this.timerStartedAt = 0;
    this.elapsedMs = 0;
    this.timerRunning = false;

    this.moveCount = 0;

    this.reducedMotion = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

    // Bound handlers so add/removeEventListener reference the same function.
    this._onVeilBeginClick = this._onVeilBeginClick.bind(this);
    this._onVeilSkipClick = this._onVeilSkipClick.bind(this);
    this._onRestartClick = this._onRestartClick.bind(this);
    this._onHintClick = this._onHintClick.bind(this);
    this._onRewardClose = this._onRewardClose.bind(this);
    this._onRewardCtaClick = this._onRewardCtaClick.bind(this);
    this._onTimerTick = this._onTimerTick.bind(this);
    this._onSectionUnload = this._onSectionUnload.bind(this);
    this._onMotionPreferenceChange = this._onMotionPreferenceChange.bind(this);

    this._readConfig();
    this._cacheDom();
    this._bindStaticListeners();
    this._observeVisibility();
  }

  /* ------------------------------------------------------------------ */
  /* Configuration                                                       */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._readConfig = function () {
    var ds = this.root.dataset;

    this.config = {
      productHandle: ds.productHandle || '',
      productTitle: ds.productTitle || '',
      productUrl: ds.productUrl || '',
      variantId: ds.variantId || '',
      imageSrc: ds.imageSrc || '',
      imageSrc2x: ds.imageSrc2x || '',
      particleTheme: ds.particleTheme || 'default',
      difficulty: ds.difficulty || 'medium',
      rewardType: ds.rewardType || 'hidden_story',
      rewardTitle: ds.rewardTitle || '',
      rewardDescription: ds.rewardDescription || '',
      rewardCoupon: ds.rewardCoupon || '',
      animationSpeed: parseInt(ds.animationSpeed, 10) || 100,
      enableSteam: ds.enableSteam === 'true',
      enableParticles: ds.enableParticles === 'true',
      enableSounds: ds.enableSounds === 'true',
      enableConfetti: ds.enableConfetti === 'true',
      boardRadius: parseInt(ds.boardRadius, 10) || 4,
      tileGap: parseInt(ds.tileGap, 10) || 3
    };

    // Duration multiplier: 100% = 1x (baseline). 200% = 0.5x (twice as
    // fast). 50% = 2x (half speed). Clamp defensively against bad input.
    var speedPct = Math.min(200, Math.max(50, this.config.animationSpeed));
    this.config.speedMultiplier = 100 / speedPct;

    this.root.style.setProperty('--mdj-board-radius', this.config.boardRadius + 'px');
    this.root.style.setProperty('--mdj-tile-gap', this.config.tileGap + 'px');
    this.root.style.setProperty('--mdj-speed-multiplier', String(this.config.speedMultiplier));

    this.config.shuffleMoves =
      DIFFICULTY_SHUFFLE_MOVES[this.config.difficulty] || DIFFICULTY_SHUFFLE_MOVES.medium;
  };

  /* ------------------------------------------------------------------ */
  /* DOM caching                                                          */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._cacheDom = function () {
    this.dom = {
      stage: this.root.querySelector('[data-mdj-stage]'),
      board: this.root.querySelector('[data-mdj-board]'),
      boardSkeleton: this.root.querySelector('[data-mdj-board-skeleton]'),
      effectsCanvas: this.root.querySelector('[data-mdj-effects-canvas]'),
      hintOverlay: this.root.querySelector('[data-mdj-hint-overlay]'),
      veil: this.root.querySelector('[data-mdj-veil]'),
      veilBeginBtn: this.root.querySelector('[data-mdj-action="begin"]'),
      veilSkipBtn: this.root.querySelector('[data-mdj-action="skip"]'),
      hud: this.root.querySelector('[data-mdj-hud]'),
      movesValue: this.root.querySelector('[data-mdj-moves-value]'),
      timeValue: this.root.querySelector('[data-mdj-time-value]'),
      restartBtn: this.root.querySelector('[data-mdj-action="restart"]'),
      hintBtn: this.root.querySelector('[data-mdj-action="hint"]'),
      liveRegion: this.root.querySelector('[data-mdj-live-region]'),
      rewardTemplate: this.root.querySelector('[data-mdj-reward-template]')
    };
  };

  /* ------------------------------------------------------------------ */
  /* Lazy boot via IntersectionObserver                                   */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._observeVisibility = function () {
    var self = this;

    if (!('IntersectionObserver' in window)) {
      // No IO support: boot immediately rather than never rendering.
      this._boot();
      return;
    }

    this.observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            self._boot();
            self.observer.disconnect();
            self.observer = null;
            break;
          }
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.01 }
    );

    this.observer.observe(this.root);
  };

  /* ------------------------------------------------------------------ */
  /* Boot sequence                                                        */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._boot = function () {
    if (this.booted || this.destroyed || !this.dom.board) return;
    this.booted = true;

    // The board is fully constructed and shuffled at boot, but stays
    // non-interactive (and out of tab order) until the veil is dismissed —
    // this keeps first paint simple while still giving the customer a
    // deliberate "Begin" moment rather than an already-scrambled board.
    if ('inert' in this.dom.board) {
      this.dom.board.inert = true;
    }

    this.storage = new MDJRitual.Storage(this.config.productHandle);

    this.shopify = new MDJRitual.Shopify(this.root, {
      productHandle: this.config.productHandle,
      productUrl: this.config.productUrl,
      variantId: this.config.variantId,
      rewardType: this.config.rewardType,
      rewardCoupon: this.config.rewardCoupon
    });

    if (this.dom.effectsCanvas) {
      this.effects = new MDJRitual.Effects(this.dom.effectsCanvas, {
        theme: this.config.particleTheme,
        enableSteam: this.config.enableSteam,
        enableParticles: this.config.enableParticles,
        reducedMotion: this.reducedMotion,
        speedMultiplier: this.config.speedMultiplier
      });
      this.effects.start('idle');
    }

    this.board = new MDJRitual.Board(this.dom.board, {
      imageSrc: this.config.imageSrc,
      imageSrc2x: this.config.imageSrc2x,
      shuffleMoves: this.config.shuffleMoves,
      tileGap: this.config.tileGap,
      reducedMotion: this.reducedMotion,
      hintOverlayEl: this.dom.hintOverlay,
      onReady: this._handleBoardReady.bind(this),
      onMove: this._handleMove.bind(this),
      onSolved: this._handleSolved.bind(this)
    });

    this.input = new MDJRitual.Input(this.dom.board, this.board, {
      reducedMotion: this.reducedMotion
    });

    this.board.init();

    this._bindDynamicListeners();
    this._loadBestResult();
  };

  /* ------------------------------------------------------------------ */
  /* Event wiring                                                         */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._bindStaticListeners = function () {
    // Static listeners are safe to attach before boot: they reference
    // controls that exist in the DOM from first render (skeleton state),
    // and internally no-op until this.board exists.
    if (this.dom.veilBeginBtn) {
      this.dom.veilBeginBtn.addEventListener('click', this._onVeilBeginClick);
    }
    if (this.dom.veilSkipBtn) {
      this.dom.veilSkipBtn.addEventListener('click', this._onVeilSkipClick);
    }
    if (this.dom.restartBtn) {
      this.dom.restartBtn.addEventListener('click', this._onRestartClick);
    }
    if (this.dom.hintBtn) {
      this.dom.hintBtn.addEventListener('click', this._onHintClick);
    }

    document.addEventListener('shopify:section:unload', this._onSectionUnload);

    if (window.matchMedia) {
      this._motionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this._motionMediaQuery.addEventListener) {
        this._motionMediaQuery.addEventListener('change', this._onMotionPreferenceChange);
      } else if (this._motionMediaQuery.addListener) {
        // Safari < 14 fallback.
        this._motionMediaQuery.addListener(this._onMotionPreferenceChange);
      }
    }
  };

  MDJRitualController.prototype._bindDynamicListeners = function () {
    // Nothing additional required today; reserved for reward-modal
    // listeners which are bound per-open in _showReward, since the modal
    // node is cloned fresh from <template> each time.
  };

  MDJRitualController.prototype._onMotionPreferenceChange = function (event) {
    this.reducedMotion = event.matches;
    if (this.board) this.board.setReducedMotion(this.reducedMotion);
    if (this.effects) this.effects.setReducedMotion(this.reducedMotion);
  };

  /* ------------------------------------------------------------------ */
  /* Board callbacks                                                      */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._handleBoardReady = function () {
    this._announce(
      this.config.productTitle
        ? 'Puzzle ready for ' + this.config.productTitle + '. Use arrow keys, drag, or tap to move tiles.'
        : 'Puzzle ready. Use arrow keys, drag, or tap to move tiles.'
    );
  };

  MDJRitualController.prototype._handleMove = function (moveInfo) {
    if (!this.timerRunning) this._startTimer();

    this.moveCount = moveInfo.totalMoves;
    if (this.dom.movesValue) {
      this.dom.movesValue.textContent = String(this.moveCount);
    }
  };

  MDJRitualController.prototype._handleSolved = function (result) {
    this._stopTimer();

    if (this.effects) this.effects.start('reward');

    if (this.config.enableConfetti) {
      this._playCompletionAnimation();
    }

    if (this.config.enableSounds) {
      this._playChime();
    }

    var best = this.storage.getBestResult();
    var isNewBest =
      !best || result.moves < best.moves || (result.moves === best.moves && result.timeMs < best.timeMs);

    if (isNewBest) {
      this.storage.saveBestResult(result);
    }

    this._announce(
      'Puzzle solved in ' + result.moves + ' moves and ' + this._formatTime(result.timeMs) + '.' +
        (isNewBest ? ' New personal best.' : '')
    );

    this._showReward(isNewBest);
  };

  /* ------------------------------------------------------------------ */
  /* Timer                                                                */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._startTimer = function () {
    this.timerRunning = true;
    this.timerStartedAt = performance.now() - this.elapsedMs;
    this.timerRAF = requestAnimationFrame(this._onTimerTick);
  };

  MDJRitualController.prototype._stopTimer = function () {
    this.timerRunning = false;
    if (this.timerRAF) {
      cancelAnimationFrame(this.timerRAF);
      this.timerRAF = null;
    }
  };

  MDJRitualController.prototype._onTimerTick = function (now) {
    if (!this.timerRunning) return;
    this.elapsedMs = now - this.timerStartedAt;
    if (this.dom.timeValue) {
      this.dom.timeValue.textContent = this._formatTime(this.elapsedMs);
    }
    this.timerRAF = requestAnimationFrame(this._onTimerTick);
  };

  MDJRitualController.prototype._formatTime = function (ms) {
    var totalSeconds = Math.floor(ms / 1000);
    var minutes = Math.floor(totalSeconds / 60);
    var seconds = totalSeconds % 60;
    return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  };

  /* ------------------------------------------------------------------ */
  /* Veil (invitation screen)                                             */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._onVeilBeginClick = function () {
    if (!this.dom.veil) return;

    this.dom.veil.classList.add('is-dismissed');

    if (this.dom.board && 'inert' in this.dom.board) {
      this.dom.board.inert = false;
    }
    if (this.dom.hud) {
      this.dom.hud.hidden = false;
    }

    this._announce(
      this.config.productTitle
        ? 'The pieces are yours. Restore ' + this.config.productTitle + ' using arrow keys, drag, or tap.'
        : 'The pieces are yours. Use arrow keys, drag, or tap to move tiles.'
    );

    // Move focus onto the board itself (not a specific tile — tile order
    // is randomized by the shuffle) so keyboard users land inside the
    // puzzle immediately rather than falling through to whatever is next
    // on the page.
    if (this.dom.board) {
      this.dom.board.setAttribute('tabindex', '-1');
      this.dom.board.focus({ preventScroll: true });
    }
  };

  MDJRitualController.prototype._onVeilSkipClick = function () {
    var el = this.root;
    var self = this;

    this._stopTimer();
    this._announce('Puzzle skipped.');

    if (this.reducedMotion) {
      el.style.display = 'none';
      this.destroy();
      return;
    }

    var startHeight = el.getBoundingClientRect().height;
    el.style.maxHeight = startHeight + 'px';
    el.style.overflow = 'hidden';
    el.classList.add('is-collapsing');

    // Force layout so the browser registers the starting max-height
    // before we change it, otherwise the transition never runs.
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;

    el.style.opacity = '0';
    el.style.maxHeight = '0px';
    el.style.paddingTop = '0px';
    el.style.paddingBottom = '0px';
    el.style.marginTop = '0px';
    el.style.marginBottom = '0px';

    window.setTimeout(function () {
      el.style.display = 'none';
      self.destroy();
    }, 480 * this.config.speedMultiplier);
  };

  /* ------------------------------------------------------------------ */
  /* Controls                                                             */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._onRestartClick = function () {
    if (!this.board) return;

    this._stopTimer();
    this.elapsedMs = 0;
    this.moveCount = 0;

    if (this.dom.timeValue) this.dom.timeValue.textContent = '0:00';
    if (this.dom.movesValue) this.dom.movesValue.textContent = '0';

    this.board.restart();
    if (this.effects) this.effects.start('idle');

    this._announce('Puzzle restarted.');
  };

  MDJRitualController.prototype._onHintClick = function () {
    if (!this.board) return;
    this.board.showHint();
    this._announce('Showing the completed composition briefly.');
  };

  /* ------------------------------------------------------------------ */
  /* Completion flourishes                                               */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._playCompletionAnimation = function () {
    if (this.reducedMotion) return;
    if (this.dom.board) {
      this.dom.board.classList.add('is-solved');
    }
  };

  MDJRitualController.prototype._playChime = function () {
    try {
      var AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      var ctx = new AudioCtx();
      var now = ctx.currentTime;
      var notes = [523.25, 659.25, 783.99]; // C5, E5, G5 — soft major triad

      notes.forEach(function (freq, i) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;

        var start = now + i * 0.09;
        var end = start + 0.5;

        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.06, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, end);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(end + 0.05);
      });

      // Release the AudioContext shortly after the notes finish playing.
      setTimeout(function () {
        if (ctx.state !== 'closed') ctx.close();
      }, 1200);
    } catch (err) {
      // Audio is a nicety, never a hard dependency. Fail silently.
    }
  };

  /* ------------------------------------------------------------------ */
  /* Reward modal                                                         */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._showReward = function (isNewBest) {
    if (!this.dom.rewardTemplate) return;

    var fragment = this.dom.rewardTemplate.content.cloneNode(true);
    var modal = fragment.querySelector('[data-mdj-reward-modal]');
    if (!modal) return;

    var titleEl = modal.querySelector('[data-mdj-reward-title]');
    var descEl = modal.querySelector('[data-mdj-reward-description]');
    var couponWrap = modal.querySelector('[data-mdj-reward-coupon]');
    var couponCodeEl = modal.querySelector('[data-mdj-reward-coupon-code]');
    var couponCopyBtn = modal.querySelector('[data-mdj-reward-coupon-copy]');
    var closeBtn = modal.querySelector('[data-mdj-reward-close]');
    var ctaBtn = modal.querySelector('[data-mdj-reward-cta]');

    if (titleEl) {
      titleEl.textContent = this.config.rewardTitle || 'The Composition is Restored';
    }
    if (descEl) {
      descEl.textContent = this.config.rewardDescription || '';
    }

    if (this.config.rewardType === 'coupon' && this.config.rewardCoupon && couponWrap && couponCodeEl) {
      couponWrap.hidden = false;
      couponCodeEl.textContent = this.config.rewardCoupon;

      if (couponCopyBtn) {
        couponCopyBtn.addEventListener('click', function () {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(this.config.rewardCoupon).then(
              function () {
                couponCopyBtn.textContent = 'Copied';
                setTimeout(function () {
                  couponCopyBtn.textContent = 'Copy Code';
                }, 2000);
              }.bind(this)
            );
          }
        }.bind(this));
      }
    }

    if (ctaBtn) {
      if (this.config.rewardType === 'buy_now') {
        ctaBtn.textContent = 'Buy Now';
      }
      ctaBtn.addEventListener('click', this._onRewardCtaClick);
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', this._onRewardClose);
    }

    modal.addEventListener('click', function (event) {
      if (event.target === modal) this._onRewardClose();
    }.bind(this));

    document.body.appendChild(modal);
    this._activeRewardModal = modal;

    // Force layout before adding the open class so the transition runs.
    // eslint-disable-next-line no-unused-expressions
    modal.offsetHeight;
    modal.classList.add('is-open');

    if (ctaBtn) ctaBtn.focus();

    this._previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  };

  MDJRitualController.prototype._onRewardClose = function () {
    if (!this._activeRewardModal) return;
    var modal = this._activeRewardModal;
    modal.classList.remove('is-open');
    document.body.style.overflow = this._previousBodyOverflow || '';

    var duration = this.reducedMotion ? 0 : 400 * this.config.speedMultiplier;
    setTimeout(function () {
      if (modal.parentNode) modal.parentNode.removeChild(modal);
    }, duration);

    this._activeRewardModal = null;
  };

  MDJRitualController.prototype._onRewardCtaClick = function () {
    if (!this.shopify) return;

    if (this.config.rewardType === 'buy_now') {
      this.shopify.buyNow();
    } else {
      this.shopify.addToCart().then(
        function () {
          this._announce('Added to cart.');
          this._onRewardClose();
        }.bind(this),
        function () {
          this._announce('Could not add to cart automatically. Please use the Add to Cart button.');
        }.bind(this)
      );
    }
  };

  /* ------------------------------------------------------------------ */
  /* Persistence                                                          */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._loadBestResult = function () {
    var best = this.storage.getBestResult();
    if (best && this.dom.liveRegion) {
      // Silent context set, not announced on load — avoids noisy screen
      // reader chatter before the user has interacted at all.
      this.dom.board.setAttribute(
        'data-mdj-best',
        best.moves + ' moves / ' + this._formatTime(best.timeMs)
      );
    }
  };

  /* ------------------------------------------------------------------ */
  /* Accessibility                                                        */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._announce = function (message) {
    if (!this.dom.liveRegion) return;
    this.dom.liveRegion.textContent = '';
    // Re-set on next frame so identical consecutive messages still fire
    // a screen reader announcement.
    requestAnimationFrame(
      function () {
        this.dom.liveRegion.textContent = message;
      }.bind(this)
    );
  };

  /* ------------------------------------------------------------------ */
  /* Lifecycle / cleanup                                                  */
  /* ------------------------------------------------------------------ */

  MDJRitualController.prototype._onSectionUnload = function (event) {
    if (!event.detail || event.detail.sectionId !== this.sectionId) return;
    this.destroy();
  };

  MDJRitualController.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;

    this._stopTimer();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.dom.veilBeginBtn) {
      this.dom.veilBeginBtn.removeEventListener('click', this._onVeilBeginClick);
    }
    if (this.dom.veilSkipBtn) {
      this.dom.veilSkipBtn.removeEventListener('click', this._onVeilSkipClick);
    }
    if (this.dom.restartBtn) {
      this.dom.restartBtn.removeEventListener('click', this._onRestartClick);
    }
    if (this.dom.hintBtn) {
      this.dom.hintBtn.removeEventListener('click', this._onHintClick);
    }

    document.removeEventListener('shopify:section:unload', this._onSectionUnload);

    if (this._motionMediaQuery) {
      if (this._motionMediaQuery.removeEventListener) {
        this._motionMediaQuery.removeEventListener('change', this._onMotionPreferenceChange);
      } else if (this._motionMediaQuery.removeListener) {
        this._motionMediaQuery.removeListener(this._onMotionPreferenceChange);
      }
    }

    if (this._activeRewardModal && this._activeRewardModal.parentNode) {
      this._activeRewardModal.parentNode.removeChild(this._activeRewardModal);
      this._activeRewardModal = null;
    }

    if (this.input) this.input.destroy();
    if (this.board) this.board.destroy();
    if (this.effects) this.effects.destroy();
    if (this.shopify) this.shopify.destroy();

    this.input = null;
    this.board = null;
    this.effects = null;
    this.shopify = null;
    this.storage = null;
  };

  /* ------------------------------------------------------------------ */
  /* Bootstrap: find every instance on the page and construct it          */
  /* ------------------------------------------------------------------ */

  MDJRitual.Core = MDJRitualController;
  MDJRitual._instances = MDJRitual._instances || [];

  function bootstrapAll() {
    var roots = document.querySelectorAll('[data-mdj-ritual]');
    for (var i = 0; i < roots.length; i++) {
      var el = roots[i];
      if (el.__mdjControllerAttached) continue;
      el.__mdjControllerAttached = true;
      MDJRitual._instances.push(new MDJRitualController(el));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapAll);
  } else {
    bootstrapAll();
  }

  // Theme editor: newly loaded sections (e.g. adding the block via the
  // customizer) need to be picked up without a full page reload.
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelector) return;
    var el = event.target.querySelector('[data-mdj-ritual]');
    if (el && !el.__mdjControllerAttached) {
      el.__mdjControllerAttached = true;
      MDJRitual._instances.push(new MDJRitualController(el));
    }
  });
})();